import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Text,
  Image,
  Icon,
  ListItem,
  SimpleGrid,
  Container,
  UnorderedList,
} from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { IconCompany, IconLetter, IconPen, IconPhone } from 'assets/icons';
import FallbackAvatar from 'components/FallbackAvatar';
import LogoLoader from 'components/Loader/LogoLoader';
import Title from 'components/Title';
import { roles } from 'global/rolesAndPermissions';
import { routePaths } from 'global/routePaths';
import { useFetchEmployeeById } from 'hooks/employee/useFetchEmployeeById';
import { useUserInfo } from 'store/useUserStore';

const EditEmployee: React.FC<unknown> = () => {
  const { t } = useTranslation(['employees', 'common']);
  const userInfo: any = useUserInfo();

  const { id } = useParams();
  const navigate = useNavigate();

  const { data, status } = useFetchEmployeeById(id!);

  if (status === 'loading') return <LogoLoader />;

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb="6">
        <Title>{t('employees:userDetails')}</Title>

        {userInfo?.roleId === roles.ADMIN ? (
          <Button
            variant="ghost"
            leftIcon={
              <Icon
                as={IconPen}
                sx={{
                  path: {
                    fill: 'primary.500',
                  },
                }}
                boxSize="12px"
              />
            }
            color="primary.500"
            fontWeight={700}
            onClick={() => navigate(routePaths.employees.edit.replace(':id', id!))}
            data-testid="button-edit-user"
          >
            {t('common:edit')}
          </Button>
        ) : null}
      </Flex>
      <Container maxWidth="7xl">
        <Flex mt="6" flexDirection={{ base: 'column', md: 'row' }} sx={{ gap: '32px' }}>
          <Box textAlign="center">
            <Image
              src={data.payload?.employee_image}
              fallback={
                <FallbackAvatar
                  data-testid="user-fallback-image"
                  name={data.payload?.firstName + ' ' + data.payload?.lastName}
                  boxSize="200px"
                  sx={{ '& div': { fontSize: '5xl' } }}
                />
              }
              alt={data.payload?.firstName + ' ' + data.payload?.lastName}
              boxSize="200px"
              objectFit="cover"
              borderRadius="md"
            />
          </Box>

          <Box flex="1 1 auto">
            <Box mb="4">
              <Text fontWeight="semibold" fontSize="2xl" data-testid="user-name">
                {data.payload?.firstName + ' ' + data.payload?.lastName}
                {data.payload?.companyEmployeeId ? (
                  <Text as="span" fontSize="lg" data-testid="user-employee-id">
                    {' '}
                    ({data.payload?.companyEmployeeId})
                  </Text>
                ) : null}
              </Text>
              <Text color="grey.700">
                {t('common:status')}:{' '}
                <Text as="span" fontWeight="semibold" data-testid="user-status">
                  {data.payload?.active}
                </Text>
              </Text>
              <Text color="grey.700" mb="3">
                {t('common:role')}:{' '}
                <Text as="span" fontWeight="semibold" data-testid="user-role">
                  {data.payload?.roleName}
                </Text>
              </Text>
              <Flex direction="row" sx={{ gap: '16px' }} wrap="wrap">
                {data.payload?.email ? (
                  <Text mb="1" data-testid="user-email">
                    <Icon as={IconLetter} mr="2" />
                    {data.payload?.email}
                  </Text>
                ) : null}
                {data.payload?.phone ? (
                  <Text data-testid="user-phone">
                    <Icon as={IconPhone} mr="2" />
                    {data.payload?.phone}
                  </Text>
                ) : null}
                {data?.payload?.company?.name ? (
                  <Text data-testid="user-">
                    <Icon as={IconCompany} sx={{ '& path': { fill: 'gray.400' } }} mr="2" />
                    {data.payload?.company?.name}
                  </Text>
                ) : null}
              </Flex>
            </Box>
            <Divider my="6" />
            {data?.payload?.groups?.length ? (
              <>
                <Box>
                  <Heading
                    as="h6"
                    mb="3"
                    fontSize="13px"
                    fontWeight="semibold"
                    color="gray.600"
                    textTransform="uppercase"
                  >
                    {t('common:groups')}
                  </Heading>
                  <UnorderedList data-testid="user-groups">
                    {data?.payload?.groups.map((item: any) => <ListItem key={item.id}>{item.name}</ListItem>)}
                  </UnorderedList>
                </Box>
                <Divider my="6" />
              </>
            ) : null}
            <Box>
              <Heading as="h6" mb="3" fontSize="13px" fontWeight="semibold" color="gray.500" textTransform="uppercase">
                {t('employees:permissions')}
              </Heading>
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing="2">
                <Flex alignItems="center" mr="5">
                  <Text data-testid="user-location-tracking">
                    {t('employees:trackLocation')}:{' '}
                    {data?.payload?.locationTracking ? (
                      <Text as="span" color="success.500" fontWeight="semibold">
                        {t('common:on')}
                      </Text>
                    ) : (
                      <Text as="span" color="secondary.500" fontWeight="semibold">
                        {t('common:off')}
                      </Text>
                    )}
                  </Text>
                </Flex>
                <Flex alignItems="center" mr="5">
                  <Text data-testid="user-track-mileage">
                    {t('employees:trackMileage')}:{' '}
                    {data?.payload?.mileageTracking ? (
                      <Text as="span" color="success.500" fontWeight="semibold">
                        {t('common:on')}
                      </Text>
                    ) : (
                      <Text as="span" color="secondary.500" fontWeight="semibold">
                        {t('common:off')}
                      </Text>
                    )}
                  </Text>
                </Flex>
                <Flex alignItems="center" mr="5">
                  <Text data-testid="user-view-reports">
                    {t('common:viewReports')}:{' '}
                    {data?.payload?.permissions?.accessReports ? (
                      <Text as="span" color="success.500" fontWeight="semibold">
                        {t('common:on')}
                      </Text>
                    ) : (
                      <Text as="span" color="secondary.500" fontWeight="semibold">
                        {t('common:off')}
                      </Text>
                    )}
                  </Text>
                </Flex>
                <Flex alignItems="center" mr="5">
                  <Text data-testid="user-manage-jobs">
                    {t('employees:manageJobs')}:{' '}
                    {data?.payload?.permissions?.manageJobs ? (
                      <Text as="span" color="success.500" fontWeight="semibold">
                        {t('common:on')}
                      </Text>
                    ) : (
                      <Text as="span" color="secondary.500" fontWeight="semibold">
                        {t('common:off')}
                      </Text>
                    )}
                  </Text>
                </Flex>
                <Flex alignItems="center" mr="5">
                  <Text data-testid="user-manage-schedules">
                    {t('employees:manageSchedules')}:{' '}
                    {data?.payload?.permissions?.manageSchedules ? (
                      <Text as="span" color="success.500" fontWeight="semibold">
                        {t('common:on')}
                      </Text>
                    ) : (
                      <Text as="span" color="secondary.500" fontWeight="semibold">
                        {t('common:off')}
                      </Text>
                    )}
                  </Text>
                </Flex>
                <Flex alignItems="center" mr="5">
                  <Text data-testid="user-manage-timesheets">
                    {t('employees:manageTime/Mileage')}:{' '}
                    {data?.payload?.permissions?.manageTimesheets ? (
                      <Text as="span" color="success.500" fontWeight="semibold">
                        {t('common:on')}
                      </Text>
                    ) : (
                      <Text as="span" color="secondary.500" fontWeight="semibold">
                        {t('common:off')}
                      </Text>
                    )}
                  </Text>
                </Flex>
                <Flex alignItems="center" mr="5">
                  <Text data-testid="user-who-is-working">
                    {t('employees:viewWhosWorking')}:{' '}
                    {data?.payload?.permissions?.whoIsWorking ? (
                      <Text as="span" color="success.500" fontWeight="semibold">
                        {t('common:on')}
                      </Text>
                    ) : (
                      <Text as="span" color="secondary.500" fontWeight="semibold">
                        {t('common:off')}
                      </Text>
                    )}
                  </Text>
                </Flex>
              </SimpleGrid>
            </Box>

            <Divider my="6" />
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default EditEmployee;
